<template>
  <div :class="['app-shell', 'env-local', 'notranslate']" translate="no">
    <DeploymentUpdateNotice :auto-reload="menuState == 0 || menuState == 3" />
    <!-- <div class="env-badge" role="status" aria-live="polite">
      <span class="env-dot"></span>
      <span class="env-label">Beta</span>
    </div> -->

    <!--
    <div id="menu">
      <button class="menu-item" @click="navigateTo(0)">Hauptbereich</button>
      <button class="menu-item" @click="navigateTo(1)">Editierbereich</button>
    </div>-->
    <c_Login
      v-show="menuState == 0"
      :session-expired-message="sessionExpiredMessage"
      @clear-session-expired-message="sessionExpiredMessage = ''"
    />
    <c_Main v-if="loggedInUser && loggedInUser.id && mountedViews.configurator" ref="configurator" v-show="menuState == 1" />
    <c_Dashboard v-if="loggedInUser && loggedInUser.id && mountedViews.dashboard" ref="dashboard" v-show="menuState == 3" />
    <c_ProfileData v-if="loggedInUser && loggedInUser.id && mountedViews.profile" ref="profile" v-show="menuState == 4" />
  </div>
</template>

<script>
import c_Login from './components/LoginComponent.vue';
import c_Main from './components/MainComponent.vue';
import c_Dashboard from './components/DashboardComponent.vue';
import c_ProfileData from './components/ProfileDataComponent.vue';
import DeploymentUpdateNotice from './components/DeploymentUpdateNotice.vue';
import '@fortawesome/fontawesome-free/css/all.css'; 
import api from '@/api/client';

const SESSION_TIMEOUT_MS = 10 * 60 * 1000;
const HEARTBEAT_INTERVAL_MS = 60 * 1000;
const ACTIVITY_EVENTS = ['click', 'keydown', 'mousemove', 'mousedown', 'scroll', 'touchstart'];
const DASHBOARD_HISTORY_GUARD_DEPTH = 20;

export default {
  name: 'App',
  components: {
      DeploymentUpdateNotice,
      c_Login,
      c_Main,
      c_Dashboard,
      c_ProfileData
  },
  data() {
    return {
      // 
      menuState: 0,
      loggedInUser: null,
      company: null,
      contactPerson: null,
      selectedProject: null,
      sessionExpiredMessage: '',
      activityTimeoutId: null,
      heartbeatIntervalId: null,
      lastActivityAt: 0,
      lastHeartbeatAt: 0,
      heartbeatInFlight: false,
      mountedViews: {
        configurator: false,
        dashboard: false,
        profile: false,
      },
    };
  },
  mounted() {
    this.replaceAppHistoryState(this.menuState);
    window.addEventListener('popstate', this.handleBrowserNavigation);
    window.addEventListener('shop-session-expired', this.handleSessionExpired);
  },
  beforeUnmount() {
    window.removeEventListener('popstate', this.handleBrowserNavigation);
    window.removeEventListener('shop-session-expired', this.handleSessionExpired);
    this.stopActivityTracking();
  },
  methods: {
    setLoggedInUser(user, token = '') {
      if (user?.id && token) {
        api.defaults.headers.common.Authorization = `Bearer ${token}`;
        this.loggedInUser = user;
        this.sessionExpiredMessage = '';
        this.startActivityTracking();
      } else {
        this.loggedInUser = null;
        this.menuState = 0;
        this.resetMountedViews();
        this.clearAuthHeader();
        this.stopActivityTracking();
        this.replaceAppHistoryState(0);
      }
    },  
    getLoggedInUser() {
      return this.loggedInUser;
    },
    patchLoggedInUser(patch) {
      if (this.loggedInUser?.id && patch) {
        this.loggedInUser = { ...this.loggedInUser, ...patch };
      }
    },
    setCompany(company) {
      this.company = company;
    },  
    getCompany() {
      return this.company;
    },
    setContactPerson(contactPerson) {
      this.contactPerson = contactPerson;
    },  
    getContactPerson() {
      return this.contactPerson;
    },
    setSelectedProject(project) {
      this.selectedProject = project;
    },
    getSelectedProject() {
      return this.selectedProject;
    },
    clearAuthHeader() {
      delete api.defaults.headers.common.Authorization;
    },
    startActivityTracking() {
      this.stopActivityTracking();
      this.lastActivityAt = Date.now();
      this.lastHeartbeatAt = Date.now();
      this.heartbeatInFlight = false;

      ACTIVITY_EVENTS.forEach((eventName) => {
        window.addEventListener(eventName, this.handleUserActivity, { passive: true });
      });

      this.resetInactivityTimer();
      this.heartbeatIntervalId = window.setInterval(() => {
        this.sendHeartbeat();
      }, HEARTBEAT_INTERVAL_MS);
    },
    stopActivityTracking() {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, this.handleUserActivity);
      });

      if (this.activityTimeoutId) {
        window.clearTimeout(this.activityTimeoutId);
        this.activityTimeoutId = null;
      }

      if (this.heartbeatIntervalId) {
        window.clearInterval(this.heartbeatIntervalId);
        this.heartbeatIntervalId = null;
      }
    },
    handleUserActivity() {
      this.lastActivityAt = Date.now();
      this.resetInactivityTimer();

      if (this.lastActivityAt - this.lastHeartbeatAt >= HEARTBEAT_INTERVAL_MS) {
        this.sendHeartbeat();
      }
    },
    resetInactivityTimer() {
      if (this.activityTimeoutId) {
        window.clearTimeout(this.activityTimeoutId);
      }

      this.activityTimeoutId = window.setTimeout(() => {
        this.expireSessionDueToInactivity();
      }, SESSION_TIMEOUT_MS);
    },
    sendHeartbeat(force = false) {
      const userId = this.loggedInUser?.id;
      if (!userId || this.heartbeatInFlight) {
        return;
      }

      const now = Date.now();
      const recentlyActive = this.lastActivityAt && now - this.lastActivityAt <= HEARTBEAT_INTERVAL_MS;
      if (!force && !recentlyActive) {
        return;
      }

      this.heartbeatInFlight = true;
      this.lastHeartbeatAt = now;
      api.post('/api/shopusers/activity/heartbeat')
        .then(() => {
          this.lastHeartbeatAt = Date.now();
        })
        .catch((error) => {
          if (error?.response?.status === 419) {
            this.forceSessionLogout(error.response.data?.message);
          } else {
            console.error('Fehler beim Aktualisieren der Aktivität: ', error);
          }
        })
        .finally(() => {
          this.heartbeatInFlight = false;
        });
    },
    expireSessionDueToInactivity() {
      const userId = this.loggedInUser?.id;
      if (!userId) {
        return;
      }

      api.post('/api/shopusers/activity/logout').catch((error) => {
        console.error('Fehler beim Abmelden nach Inaktivität: ', error);
      });

      this.forceSessionLogout('Sie wurden nach längerer Inaktivität abgemeldet. Bitte melden Sie sich erneut an.');
    },
    handleSessionExpired(event) {
      this.forceSessionLogout(event?.detail?.message);
    },
    logoutUser() {
      api.post('/api/shopusers/activity/logout').catch((error) => {
        console.error('Fehler beim Abmelden: ', error);
      });

      this.stopActivityTracking();
      this.clearAuthHeader();
      this.loggedInUser = null;
      this.company = null;
      this.contactPerson = null;
      this.selectedProject = null;
      this.resetMountedViews();
      this.$refs.dashboard?.stopActivitySummaryPolling?.();
      this.sessionExpiredMessage = '';
      this.menuState = 0;
      this.replaceAppHistoryState(0);

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
    forceSessionLogout(message = 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.') {
      this.stopActivityTracking();
      this.clearAuthHeader();
      this.loggedInUser = null;
      this.company = null;
      this.contactPerson = null;
      this.selectedProject = null;
      this.resetMountedViews();
      this.$refs.dashboard?.stopActivitySummaryPolling?.();
      this.sessionExpiredMessage = message || 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.';
      this.menuState = 0;
      this.replaceAppHistoryState(0);

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
    navigateTo(route) {
      // Logik für die Navigation.
      // Sie könnten eine if-Abfrage verwenden, um zu prüfen, welche Route angefragt wurde, 
      // und dann die entsprechende Aktion ausführen.
      this.changeMenuState(route);
    },
    changeMenuState(menuState, options = {}) {
      const previousMenuState = this.menuState;
      const wasDashboard = this.menuState == 3;
      if (wasDashboard && menuState != 3) {
        this.$refs.dashboard?.stopActivitySummaryPolling?.();
      }

      if (menuState == 1) {
        this.mountedViews.configurator = true;
      }
      else if (menuState == 3) {
        this.mountedViews.dashboard = true;
      }
      else if (menuState == 4) {
        this.mountedViews.profile = true;
      }

      this.menuState = menuState;
      if (!options.fromHistory) {
        if (previousMenuState == 0 && menuState == 3) {
          this.resetDashboardHistoryRoot();
        } else {
          this.syncAppHistoryState(menuState, previousMenuState, options);
        }
        if (menuState == 3 && previousMenuState != 0) {
          this.ensureDashboardHistoryGuard();
        }
      }

      this.$nextTick(() => {
        if (options.fromHistory && this.menuState == 1) {
          this.$refs.configurator?.applyHistoryState?.(options.historyState || {});
        }
        else if (this.menuState == 1) {
          this.$refs.configurator?.refreshProject?.();
        }
        else if (this.menuState == 3) {
          this.$refs.dashboard?.refreshDashboard?.();
        }
        else if (this.menuState == 4) {
          this.$refs.profile?.refreshProfileData?.();
        }

        // Immer nach dem State nach oben scrollen
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      });
    },
    handleBrowserNavigation(event) {
      if (!event.state || event.state.msShop !== true) {
        if (this.loggedInUser?.id && this.menuState == 3) {
          this.ensureDashboardHistoryGuard();
        }
        return;
      }

      const targetView = event.state.view || this.menuStateToHistoryView(event.state.menuState);
      let targetMenuState = this.historyViewToMenuState(targetView);
      if (!Number.isFinite(targetMenuState)) {
        targetMenuState = this.loggedInUser?.id ? 3 : 0;
      }

      if (!this.loggedInUser?.id) {
        targetMenuState = 0;
      } else if (targetMenuState === 0) {
        targetMenuState = 3;
      }

      if (targetMenuState === 3 && this.menuState == 3) {
        this.ensureDashboardHistoryGuard();
        return;
      }

      this.changeMenuState(targetMenuState, { fromHistory: true, historyState: event.state });
      if (targetMenuState === 3) {
        this.ensureDashboardHistoryGuard();
      }
    },
    syncAppHistoryState(menuState, previousMenuState, options = {}) {
      if (!window.history?.pushState || !window.history?.replaceState) {
        return;
      }
      if (menuState == 1) {
        return;
      }

      const currentState = window.history.state;
      const shouldReplace =
        options.replaceHistory ||
        (previousMenuState == 0 && menuState == 3) ||
        (currentState?.msShop === true && Number(currentState.menuState) === Number(menuState));
      const method = shouldReplace ? 'replaceState' : 'pushState';

      window.history[method](
        this.appHistoryState(menuState, { view: this.menuStateToHistoryView(menuState) }),
        '',
        window.location.href
      );
    },
    ensureDashboardHistoryGuard() {
      if (this.menuState != 3) {
        return;
      }

      this.pushAppHistoryState(3, DASHBOARD_HISTORY_GUARD_DEPTH, { view: 'dashboard' });
    },
    resetDashboardHistoryRoot() {
      this.replaceAppHistoryState(3);
      this.ensureDashboardHistoryGuard();
      window.setTimeout(() => this.ensureDashboardHistoryGuard(), 0);
      window.setTimeout(() => this.ensureDashboardHistoryGuard(), 150);
      window.setTimeout(() => this.ensureDashboardHistoryGuard(), 500);
    },
    pushAppHistoryState(menuState, depth = 1, extraState = {}) {
      if (!window.history?.pushState || !this.loggedInUser?.id) {
        return;
      }

      for (let index = 0; index < depth; index += 1) {
        try {
          window.history.pushState(
            this.appHistoryState(menuState, extraState),
            '',
            window.location.href
          );
        } catch (error) {
          console.error('Browser-Historie konnte nicht erweitert werden:', error);
          return;
        }
      }
    },
    pushAppViewHistory(view, extraState = {}, options = {}) {
      if (!window.history?.pushState || !window.history?.replaceState || !this.loggedInUser?.id) {
        return;
      }

      const menuState = this.historyViewToMenuState(view);
      const method = options.replaceHistory ? 'replaceState' : 'pushState';
      window.history[method](
        this.appHistoryState(menuState, { ...extraState, view }),
        '',
        window.location.href
      );
    },
    replaceAppHistoryState(menuState) {
      if (!window.history?.replaceState) {
        return;
      }

      window.history.replaceState(
        this.appHistoryState(menuState, { view: this.menuStateToHistoryView(menuState) }),
        '',
        window.location.href
      );
    },
    appHistoryState(menuState, extraState = {}) {
      return {
        ...(window.history?.state || {}),
        ...extraState,
        msShop: true,
        menuState: Number(menuState),
      };
    },
    menuStateToHistoryView(menuState) {
      const normalizedMenuState = Number(menuState);
      if (normalizedMenuState === 3) {
        return 'dashboard';
      }
      if (normalizedMenuState === 4) {
        return 'profile';
      }
      if (normalizedMenuState === 1) {
        return 'position';
      }
      return 'login';
    },
    historyViewToMenuState(view) {
      if (view === 'dashboard') {
        return 3;
      }
      if (view === 'profile') {
        return 4;
      }
      if (view === 'cart' || view === 'position') {
        return 1;
      }
      return 0;
    },
    resetMountedViews() {
      this.mountedViews = {
        configurator: false,
        dashboard: false,
        profile: false,
      };
    }
  },
  computed: {
    stage() {
      return (process.env.VUE_APP_STAGE || 'LOCAL').toUpperCase();
    },
    stageClass() {
      return `env-${this.stage.toLowerCase()}`;
    },
    stageLabel() {
      return this.stage;
    },
  }
}
</script>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}

.app-shell {
  position: relative;
  min-height: 100vh;
}

.env-badge {
  position: fixed;
  top: 12px;
  left: 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  border-radius: 999px;
  color: #ffffff;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  z-index: 2000;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.16);
  background: linear-gradient(120deg, #334155, #475569);
}

.env-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 0 0 6px rgba(255, 255, 255, 0.18);
}

.env-beta .env-badge {
  background: linear-gradient(120deg, #b91c1c, #ef4444);
}

.env-live .env-badge {
  background: linear-gradient(120deg, #065f46, #10b981);
}

.env-local .env-badge {
  background: linear-gradient(120deg, #334155, #475569);
}

#menu {
  display: flex;
  justify-content: space-around;
  background-color: #f8f9fa;
  padding: 10px 0;
}

.menu-item {
  margin: 0 20px;
  padding: 10px 20px;
  border: none;
  background-color: #007bff;
  color: white;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.menu-item:hover {
  background-color: #f6f6f6;
  color: #333333;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}
</style>
